import {
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconCalendar,
  IconCalendarEvent,
  IconCalendarPlus,
  IconChecklist,
  IconFilePlus,
  IconMapPin,
  IconPhone,
  IconUrgent,
  IconVideo,
  IconVideoPlus,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMedplum } from '@medplum/react';
import { FEATURE_VISIBILITY } from '../../../config/featureVisibility';
import { LocalhostOnlyBadge } from '../../../components/LocalhostOnlyBadge';
import { patientContactToContact, relatedPersonToContact } from '../utils/clientProfileFhir';
import { contactDisplayName, contactDisplayPhone } from '../utils/contactDisplayUtils';
import type { Contact } from '../data/contactTypes';
import { ContactRoiBadge } from '../components/ContactRoiBadge';
import { ContactRoiModal } from '../components/ContactRoiModal';
import { usePatientRoiConsents } from '../hooks/usePatientRoiConsents';
import { resolveContactsWithRoi } from '../utils/contactRoiUtils';
import type { EpisodeChartInteraction } from '../../../hooks/useEpisodeScopedClientChartBundle';
import { isAppointmentPostSession } from '../utils/appointmentPostSession';
import { buildMockAppointmentPageState } from '../../appointments/mockAppointmentPageData';
import type { Client } from '../../caseload/data';
import { getAppointmentChartAccess } from '../../../lib/rbac/eocChartScope';
import { formatUsDateShort } from '../../../lib/dateHelpers';
import {
  isEmergencyContactRelatedPerson,
} from '../../../lib/fhirEmergencyContact';
import {
  chartFormTaskStatusColor,
  chartFormTaskStatusLabel,
} from '../../../lib/clientFacingTasks';
import type { Appointment, ClinicalNote, Episode, Task } from '../data';

const MOCK_STATE_KEY = 'mockAppointmentData';

interface OverviewTabProps {
  client: Client;
  episodes: Episode[];
  appointments: Appointment[];
  notes?: ClinicalNote[];
  tasks: Task[];
  /** When true, show loading skeleton instead of content */
  loading?: boolean;
  /** When set, show error state with optional retry */
  error?: string | null;
  onRetry?: () => void;
  /** Medplum pilot: gate opening appointment detail from this overview. */
  episodeInteraction?: EpisodeChartInteraction;
}

/** Minimum height for cards so they align when one column is empty; natural flow otherwise */
const CARD_MIN_HEIGHT = 200;

/**
 * Overview tab: upcoming appointments and outstanding documentation/forms
 */
export function OverviewTab({
  client,
  appointments,
  tasks,
  loading = false,
  error = null,
  onRetry,
  episodeInteraction,
}: OverviewTabProps): JSX.Element {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const medplum = useMedplum();
  const { consents, refresh: refreshRoiConsents } = usePatientRoiConsents(client.id);
  const [emergencyContactBase, setEmergencyContactBase] = useState<Contact[]>([]);
  const [roiContact, setRoiContact] = useState<Contact | null>(null);

  const openRoiView = (c: Contact) => { setRoiContact(c); };
  const closeRoi = () => setRoiContact(null);

  const emergencyContacts = useMemo(
    () => resolveContactsWithRoi(emergencyContactBase, consents),
    [emergencyContactBase, consents],
  );

  useEffect(() => {
    if (!client.id) {
      setEmergencyContactBase([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const patientRef = `Patient/${client.id}`;
        const [patient, rps] = await Promise.all([
          medplum.readResource('Patient', client.id).catch(() => undefined),
          medplum.searchResources('RelatedPerson', { patient: patientRef }),
        ]);
        if (cancelled) return;
        const fromPatientContact = (patient?.contact ?? [])
          .map((pc, i) => patientContactToContact(pc, i))
          .filter((c) => c.isEmergencyContact);
        const fromRelatedPerson = rps
          .filter((rp) => rp.active !== false)
          .filter(isEmergencyContactRelatedPerson)
          .map(relatedPersonToContact);
        setEmergencyContactBase([...fromPatientContact, ...fromRelatedPerson]);
      } catch {
        if (!cancelled) setEmergencyContactBase([]);
      }
    })();
    return () => { cancelled = true; };
  }, [medplum, client.id]);

  const upcomingAppointments = appointments
    .filter((apt) => apt.status === 'Scheduled' && !isAppointmentPostSession(apt))
    .slice(0, 5);

  const openAppointmentPage = (apt: Appointment) => {
    const access = episodeInteraction
      ? getAppointmentChartAccess(
          apt.episodeId,
          false,
          episodeInteraction.authorizedEpisodeIds,
          episodeInteraction.patientEpisodeCount,
          episodeInteraction.viewScope,
        )
      : 'full';
    const mockState = buildMockAppointmentPageState(client, apt, appointments, {
      appointmentOnly: access === 'appointment-only',
    });
    navigate(`/dashboard/appointments/${apt.id}?patientId=${encodeURIComponent(client.id)}`, {
      state: { [MOCK_STATE_KEY]: mockState },
    });
  };

  const openFormsTab = (task?: Task) => {
    setSearchParams(task ? { tab: 'forms', taskId: task.id } : { tab: 'forms' });
  };

  const handleTaskAction = (task: Task) => {
    openFormsTab(task);
  };

  const ICON_SIZE = 36;

  if (loading) {
    return (
      <Stack gap="xl">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder style={{ minHeight: CARD_MIN_HEIGHT }}>
              <Group gap="xs" mb="md">
                <Box style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-3)' }} />
                <Box style={{ width: 160, height: 20, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-2)' }} />
              </Group>
              <Stack gap="md">
                {[1, 2, 3].map((i) => (
                  <Box key={i} p="md" style={{ borderRadius: 8, backgroundColor: 'var(--mantine-color-gray-1)' }}>
                    <Box style={{ height: 14, width: '60%', borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-2)' }} />
                    <Box style={{ height: 12, width: '40%', marginTop: 8, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-2)' }} />
                  </Box>
                ))}
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Card withBorder style={{ minHeight: CARD_MIN_HEIGHT }}>
              <Group gap="xs" mb="md">
                <Box style={{ width: 18, height: 18, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-3)' }} />
                <Box style={{ width: 80, height: 20, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-2)' }} />
              </Group>
              <Box style={{ height: 120, borderRadius: 4, backgroundColor: 'var(--mantine-color-gray-1)' }} />
            </Card>
          </Grid.Col>
        </Grid>
      </Stack>
    );
  }

  if (error) {
    return (
      <Card withBorder p="xl">
        <Stack align="center" gap="md">
          <ThemeIcon size={48} radius="xl" color="red" variant="light">
            <IconCalendar size={24} />
          </ThemeIcon>
          <Text c="dimmed" ta="center">
            {error}
          </Text>
          {onRetry && (
            <Button variant="light" onClick={onRetry}>
              Try again
            </Button>
          )}
        </Stack>
      </Card>
    );
  }

  return (
    <Stack gap="xl">
      <Grid style={{ alignItems: 'stretch' }}>
        {/* Upcoming Appointments - left column */}
        <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
          <Card
            withBorder
            style={{ flex: 1, minHeight: CARD_MIN_HEIGHT, display: 'flex', flexDirection: 'column' }}
          >
            <Group justify="space-between" mb="md">
              <Group gap="xs">
                <IconCalendar size={18} color="var(--mantine-color-gray-6)" />
                <Title order={4}>Upcoming Appointments</Title>
              </Group>
            </Group>
            {upcomingAppointments.length === 0 ? (
              <Stack align="center" gap="md" style={{ flex: 1, justifyContent: 'center', padding: 'var(--mantine-spacing-lg)' }}>
                <ThemeIcon size={56} radius="xl" variant="light" color="gray">
                  <IconCalendar size={28} />
                </ThemeIcon>
                <Text c="gray.6" size="sm" ta="center">
                  No upcoming appointments scheduled
                </Text>
                <Button
                  variant="light"
                  leftSection={<IconCalendarPlus size={16} />}
                  onClick={() => navigate('/dashboard/calendar')}
                >
                  Schedule appointment
                </Button>
              </Stack>
            ) : (
              <Stack gap="sm">
                {upcomingAppointments.map((apt) => {
                  const isTelehealth = apt.location.type === 'Telehealth';
                  const locationLabel = isTelehealth ? (apt.location.platform || 'Telehealth') : 'In Person';
                  return (
                    <Paper
                      key={apt.id}
                      p="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--mantine-color-gold-0)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                      onClick={() => openAppointmentPage(apt)}
                    >
                      <Group wrap="nowrap" align="center" gap={0}>
                        <Box w={44} miw={44} style={{ flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <IconCalendarEvent size={ICON_SIZE} style={{ strokeWidth: 1.4, color: 'var(--mantine-color-blue-6)' }} />
                        </Box>
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="sm" fw={600} truncate>
                            {apt.serviceType}
                          </Text>
                          <Group gap="xs" align="center" wrap="nowrap" mt={2} style={{ minWidth: 0, overflow: 'hidden' }}>
                            <Text size="xs" c="gray.5" style={{ flexShrink: 0 }}>
                              {formatUsDateShort(apt.date) || '--'}
                            </Text>
                            {apt.time && (
                              <>
                                <Text size="xs" c="gray.4" style={{ flexShrink: 0 }}>•</Text>
                                <Text size="xs" c="gray.5" style={{ flexShrink: 0 }}>{apt.time}</Text>
                              </>
                            )}
                            <Text size="xs" c="gray.4" style={{ flexShrink: 0 }}>•</Text>
                            <Box style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Text size="xs" c="gray.6" truncate>{apt.provider}</Text>
                            </Box>
                            <Text size="xs" c="gray.4" style={{ flexShrink: 0 }}>•</Text>
                            {isTelehealth ? (
                              <IconVideo size={13} style={{ color: 'var(--mantine-color-blue-5)', flexShrink: 0 }} />
                            ) : (
                              <IconMapPin size={13} style={{ color: 'var(--mantine-color-gray-5)', flexShrink: 0 }} />
                            )}
                            <Box style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              <Text size="xs" c="gray.6" truncate>
                                {locationLabel}
                              </Text>
                            </Box>
                          </Group>
                        </Box>
                        {isTelehealth && (
                          <Button
                            variant="light"
                            size="xs"
                            leftSection={<IconVideoPlus size={14} />}
                            ml="md"
                            style={{ flexShrink: 0 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Join
                          </Button>
                        )}
                      </Group>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </Card>
        </Grid.Col>

        {/* Right column: Tasks */}
        <Grid.Col span={{ base: 12, md: 6 }} style={{ display: 'flex' }}>
          <Card
            withBorder
            style={{
              flex: 1,
              minHeight: CARD_MIN_HEIGHT,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Group justify="space-between" mb="md" style={{ flexShrink: 0 }}>
              <Group gap="xs">
                <IconChecklist size={18} color="var(--mantine-color-gray-6)" />
                <Title order={4}>Tasks</Title>
              </Group>
            </Group>
            {tasks.length === 0 ? (
              <Stack align="center" gap="md" style={{ flex: 1, justifyContent: 'center', padding: 'var(--mantine-spacing-lg)' }}>
                <ThemeIcon size={56} radius="xl" variant="light" color="gray">
                  <IconChecklist size={28} />
                </ThemeIcon>
                <Text c="gray.6" size="sm" ta="center">
                  No form tasks
                </Text>
                <Group gap="xs">
                  <Button
                    variant="light"
                    leftSection={<IconFilePlus size={16} />}
                    onClick={() => openFormsTab()}
                  >
                    Request form
                  </Button>
                  <Button
                    variant="light"
                    leftSection={<IconCalendarPlus size={16} />}
                    onClick={() => navigate('/dashboard/calendar')}
                  >
                    Schedule appointment
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Table highlightOnHover style={{ flexShrink: 0, tableLayout: 'fixed', width: '100%' }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Task</Table.Th>
                    <Table.Th w={120}>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tasks.map((task) => (
                    <Table.Tr
                      key={task.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleTaskAction(task)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleTaskAction(task);
                        }
                      }}
                      tabIndex={0}
                    >
                      <Table.Td style={{ maxWidth: 0 }}>
                        <Text size="sm" fw={500} truncate title={task.name}>
                          {task.name}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ whiteSpace: 'nowrap', width: 120 }}>
                        <Badge
                          variant="light"
                          size="sm"
                          tt="none"
                          color={chartFormTaskStatusColor(task.status)}
                        >
                          {chartFormTaskStatusLabel(task.status)}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Card>
        </Grid.Col>
      </Grid>

      {FEATURE_VISIBILITY.chartEmergencyContacts ? (
      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconUrgent size={18} color="var(--mantine-color-red-6)" />
            <Title order={4}>Emergency Contacts</Title>
            <LocalhostOnlyBadge />
          </Group>
          <Button
            variant="subtle"
            size="xs"
            onClick={() => navigate(`/dashboard/clients/${client.id}/profile?tab=contacts`)}
          >
            Manage contacts
          </Button>
        </Group>
        {emergencyContacts.length === 0 ? (
          <Text size="sm" c="gray.6">
            No emergency contacts on file.
          </Text>
        ) : (
          <Stack gap="sm">
            {emergencyContacts.map((ec) => (
              <Paper
                key={ec.id}
                p="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/dashboard/clients/${client.id}/profile?tab=contacts`)}
              >
                <Group justify="space-between" wrap="nowrap" align="center">
                  <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={600}>{contactDisplayName(ec)}</Text>
                    <Text size="xs" c="dimmed">{ec.relationshipType}</Text>
                    <ContactRoiBadge
                      contact={ec}
                      onView={() => openRoiView(ec)}
                    />
                  </Stack>
                  {contactDisplayPhone(ec) && (
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                      <IconPhone size={14} color="var(--mantine-color-gray-6)" />
                      <Text size="sm">{contactDisplayPhone(ec)}</Text>
                    </Group>
                  )}
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Card>
      ) : null}

      {roiContact && (
        <ContactRoiModal
          opened={roiContact !== null}
          onClose={closeRoi}
          contact={roiContact}
          patientId={client.id}
          onSaved={async () => {
            closeRoi();
            await refreshRoiConsents();
          }}
        />
      )}
    </Stack>
  );
}
